import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/ServiceSection-css/ServiceSection.css'; 
import { FaTimes } from 'react-icons/fa';
import { allServices, subServices, serviceRoutes } from './SubServices'; // Data import
import Images from './Images'; // Image component import

const ServiceSection = ({ searchTerm }) => {
  const [activeService, setActiveService] = useState(null);
  const navigate = useNavigate();
  const serviceRefs = useRef({});
  const modalRef = useRef(null);

  // PERFORMANCE: useMemo prevents re-filtering on every render
  const filteredServices = useMemo(() => {
    const term = searchTerm?.toLowerCase() || '';
    if (!term) return allServices;
    return allServices.filter(service =>
      service.name.toLowerCase().includes(term)
    );
  }, [searchTerm]);

  // EFFECT: Handles highlighting the first search result
  useEffect(() => {
    if (searchTerm && filteredServices.length > 0) {
      const firstMatchName = filteredServices[0].name.toLowerCase();
      const ref = serviceRefs.current[firstMatchName];
      if (ref) {
        ref.scrollIntoView({ behavior: 'smooth', block: 'center' });
        ref.classList.add('highlight');
        setTimeout(() => {
          ref.classList.remove('highlight');
        }, 2500);
      }
    }
  }, [searchTerm, filteredServices]);

  // ACCESSIBILITY: Manages focus when the modal opens
  useEffect(() => {
    if (activeService && modalRef.current) {
      modalRef.current.focus();
    }
  }, [activeService]);

  const handleServiceClick = (serviceName) => setActiveService(serviceName);

  const handleSubserviceClick = (subserviceName) => {
    const route = serviceRoutes[subserviceName];
    if (route) {
      navigate(route);
    } else {
      alert(`No page found for ${subserviceName}`);
    }
  };

  const closeModal = () => setActiveService(null);

  // ACCESSIBILITY: Allows keyboard activation of clickable divs
  const handleKeyDown = (e, callback) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      callback();
    }
  };

  return (
    <div className="service-section-wrapper">
      <div className="service-section">
        <div className="service-content-container">
          <div className="services-container">
            <h3>SERVICES AT YOUR DOORSTEP</h3>
            <div className="service-icons">
              {filteredServices.map((service) => (
                <div
                  key={service.name}
                  className="service-item"
                  ref={(el) => (serviceRefs.current[service.name.toLowerCase()] = el)}
                  onClick={() => handleServiceClick(service.name)}
                  role="button"
                  tabIndex="0"
                  onKeyDown={(e) => handleKeyDown(e, () => handleServiceClick(service.name))}
                >
                  <div className="service-icon">{service.icon}</div>
                  <p>{service.name}</p>
                </div>
              ))}
            </div>
            <button className="explore-button" onClick={() => navigate('/services')}>
              Explore All Services
            </button>
          </div>
          
          {/* Using the separate, clean component for images */}
          <Images />

        </div>

        {activeService && (
          <div className="modal-overlay" onClick={closeModal}>
            <div
              className="modal-box"
              onClick={(e) => e.stopPropagation()}
              ref={modalRef}
              tabIndex="-1"
              aria-modal="true"
              role="dialog"
            >
              <button className="close-btn" onClick={closeModal} aria-label="Close modal">
                <FaTimes />
              </button>
              <h2>{activeService} Services</h2>
              <div className="subservice-grid">
                {subServices[activeService]?.map((sub) => (
                  <div
                    key={sub.name}
                    className="subservice-card"
                    onClick={() => handleSubserviceClick(sub.name)}
                    role="button"
                    tabIndex="0"
                    onKeyDown={(e) => handleKeyDown(e, () => handleSubserviceClick(sub.name))}
                  >
                    <div className="subservice-icon">{sub.icon}</div>
                    <p>{sub.name}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ServiceSection;